import type { PadButton } from '../emulator/types';
import { DEFAULT_KEYMAP, type KeyMap } from './InputManager';

/** キーコンフィグ UI で割り当てるボタン（スティッククリックは除外） */
export const KEYMAP_BUTTONS = [
  'UP',
  'DOWN',
  'LEFT',
  'RIGHT',
  'A',
  'B',
  'X',
  'Y',
  'L',
  'R',
  'L2',
  'R2',
  'START',
  'SELECT',
] as const satisfies readonly PadButton[];

export type KeymapButton = (typeof KEYMAP_BUTTONS)[number];

export const KEYMAP_BUTTON_LABELS: Record<KeymapButton, string> = {
  UP: '上',
  DOWN: '下',
  LEFT: '左',
  RIGHT: '右',
  A: 'A',
  B: 'B',
  X: 'X',
  Y: 'Y',
  L: 'L',
  R: 'R',
  L2: 'L2',
  R2: 'R2',
  START: 'START',
  SELECT: 'SELECT',
};

/** メニュー / 早送り等に使うためゲーム入力へ割り当て不可 */
export const RESERVED_KEY_CODES = new Set(['Escape', 'Tab']);

/** 押しっぱなし早送りの既定キーボード */
export const FAST_FORWARD_KEY_CODE = 'Tab';

/** 押しっぱなし早送りの既定ゲームパッドボタン（standard mapping の R3） */
export const FAST_FORWARD_GAMEPAD_BUTTON = 11;

export function cloneKeymap(map: KeyMap = DEFAULT_KEYMAP): KeyMap {
  return { ...map };
}

export function codesForButton(map: KeyMap, button: PadButton): string[] {
  return Object.entries(map)
    .filter(([, b]) => b === button)
    .map(([code]) => code)
    .sort();
}

/** KeyboardEvent.code を短い表示名にする */
export function formatKeyCode(code: string): string {
  const special: Record<string, string> = {
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    Space: 'Space',
    Enter: 'Enter',
    Backspace: 'Backspace',
    ShiftLeft: 'Shift (左)',
    ShiftRight: 'Shift (右)',
    ControlLeft: 'Ctrl (左)',
    ControlRight: 'Ctrl (右)',
    AltLeft: 'Alt (左)',
    AltRight: 'Alt (右)',
    Tab: 'Tab',
    Minus: '-',
    Equal: '=',
    BracketLeft: '[',
    BracketRight: ']',
    Semicolon: ';',
    Quote: "'",
    Comma: ',',
    Period: '.',
    Slash: '/',
    Backslash: '\\',
    Backquote: '`',
  };
  if (special[code]) return special[code];
  if (code.startsWith('Key') && code.length === 4) return code.slice(3);
  if (code.startsWith('Digit') && code.length === 6) return code.slice(5);
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  return code;
}

/**
 * キーをボタンへ割り当てる。同じ code が他ボタンにあれば外す。
 * 戻り値: 新しい keymap / 拒否理由
 */
export function bindKey(
  map: KeyMap,
  button: PadButton,
  code: string,
): { ok: true; map: KeyMap } | { ok: false; reason: string } {
  if (RESERVED_KEY_CODES.has(code)) {
    return { ok: false, reason: 'このキーはメニュー用に予約されています' };
  }
  if (!code) return { ok: false, reason: '無効なキーです' };

  const next: KeyMap = { ...map };
  // すでに同じボタンへ付いているなら何もしない
  if (next[code] === button) return { ok: true, map: next };
  next[code] = button;
  return { ok: true, map: next };
}

export function unbindKey(map: KeyMap, code: string): KeyMap {
  const next = { ...map };
  delete next[code];
  return next;
}

export function mergeKeymap(stored: KeyMap | undefined): KeyMap {
  if (!stored || typeof stored !== 'object') return cloneKeymap();
  return { ...stored };
}
